import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const ImportCommunePage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  
  // État pour la recherche et sélection de commune
  const [searchInput, setSearchInput] = useState('');
  const [communeSuggestions, setCommuneSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  // État pour la localisation sélectionnée
  const [filterLocation, setFilterLocation] = useState('');
  const [communeName, setCommuneName] = useState('');
  const [communeInseeCode, setCommuneInseeCode] = useState('');
  const [userPosition, setUserPosition] = useState(null);
  const [selectedCommuneLines, setSelectedCommuneLines] = useState([]); // Toutes les lignes de la commune sélectionnée
  
  // État pour le rayon et les codes postaux à proximité
  const [filterDistance, setFilterDistance] = useState(0);
  const [debouncedFilterDistance, setDebouncedFilterDistance] = useState(3);
  const [nearbyPostalCodes, setNearbyPostalCodes] = useState([]);
  const [postalCodesLoading, setPostalCodesLoading] = useState(false);
  const [showPostalCodesList, setShowPostalCodesList] = useState(false);
  
  // État pour l'inclusion/exclusion
  const [includeMode, setIncludeMode] = useState(true);
  
  // État pour la fraîcheur des jobs
  const [freshnessDays, setFreshnessDays] = useState(90);
  
  // État pour l'import
  const [loadingImport, setLoadingImport] = useState(false);
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');
  const [importStats, setImportStats] = useState(null);
  
  // État pour la carte
  const [communesPolygons, setCommunesPolygons] = useState([]);
  const [loadingPolygons, setLoadingPolygons] = useState(false);
  const [eventsCount, setEventsCount] = useState({});
  const [showMarkers, setShowMarkers] = useState(false);
  const [tileProvider, setTileProvider] = useState('osm');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const radiusCircleRef = useRef(null);

  // Récupérer le fond de carte configuré par l'administrateur
  useEffect(() => {
    const fetchTileParameter = async () => {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'tile')
        .single();

      if (!error && data?.value) {
        setTileProvider(data.value);
      }
    };

    fetchTileParameter();
  }, []);
  
  // État pour les statistiques des communes
  const [communesStats, setCommunesStats] = useState(null);
  
  // État pour la liste paginée des communes incluses
  const [includedCommunes, setIncludedCommunes] = useState([]);
  const [loadingIncludedCommunes, setLoadingIncludedCommunes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingCommune, setEditingCommune] = useState(null);
  const ITEMS_PER_PAGE = 20;

  const isTimeoutError = (error) => error?.code === '57014';

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        } else {
          setCurrentUser(user);
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
      } finally {
        setAuthChecked(true);
      }
    };
    
    getUser();
  }, []);

  // Fonction pour charger les statistiques des communes
  const loadCommunesStats = async () => {
    try {
      // Étape 1: récupérer un échantillon des valeurs DISTINCT de freshness (inclure=true)
      // On réduit le volume pour éviter le timeout
      const { data: freshnessSample, error: freshnessError } = await supabase
        .from('communes')
        .select('freshness')
        .eq('include', true)
        .neq('ignore', true)
        .limit(1000);

      if (freshnessError) {
        console.error("Erreur lors de la récupération des freshness:", freshnessError);
        if (isTimeoutError(freshnessError)) {
          setStatus('info');
          setMessage(t('common.error'));
        }
        return;
      }

      // Construire l'ensemble des valeurs distinctes observées
      const distinctSet = new Set((freshnessSample || []).map(r => r.freshness));
      // Assurer le comptage des valeurs null/non définies
      if (!distinctSet.has(null)) distinctSet.add(null);
      const distinctValues = Array.from(distinctSet);

      // Étape 2: compter par valeur de freshness (requêtes COUNT côté DB)
      // Utilise head:true pour ne pas transférer de lignes, uniquement le count
      const countPromises = distinctValues.map(async (val) => {
        const baseQuery = supabase
          .from('communes')
          .select('code_postal', { count: 'exact', head: true })
          .eq('include', true);

        const finalQuery = (val === null)
          ? baseQuery.is('freshness', null)
          : baseQuery.eq('freshness', val);

        const { count, error } = await finalQuery;
        if (error) {
          console.error('Erreur COUNT pour freshness', val, ':', error);
          return { val, count: 0 };
        }
        return { val, count: count || 0 };
      });

      const counts = await Promise.all(countPromises);

      // Agréger les résultats pour l'affichage
      const byFreshness = counts.reduce((acc, { val, count }) => {
        const key = (val === null || typeof val === 'undefined') ? 'Non défini' : String(val);
        acc[key] = (acc[key] || 0) + count;
        return acc;
      }, {});

      const total = counts.reduce((sum, c) => sum + (c.count || 0), 0);

      setCommunesStats({
        total,
        byFreshness
      });
    } catch (error) {
      console.error("Erreur lors du chargement des stats communes:", error);
    }
  };

  // Fonction pour charger les communes incluses avec pagination
  const loadIncludedCommunes = async (page = 1) => {
    setLoadingIncludedCommunes(true);
    try {
      const from = (page - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      // Compter le total
      const { data, count, error } = await supabase
        .from('communes')
        .select('code_postal, nom_de_la_commune, code_commune_insee, freshness, include', { count: 'estimated' })
        .eq('include', true)
        .neq('ignore', true)
        .order('code_postal', { ascending: true })
        .range(from, to);

      if (error) {
        console.error('Erreur lors de la récupération des communes:', error);
        if (isTimeoutError(error)) {
          setStatus('error');
          setMessage(t('common.error'));
        }
        return;
      }

      const total = typeof count === 'number' ? count : (data?.length || 0);
      setTotalPages(Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)));
      setIncludedCommunes(data || []);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erreur lors du chargement des communes:', error);
      if (isTimeoutError(error)) {
        setStatus('error');
        setMessage(t('common.error'));
      }
    } finally {
      setLoadingIncludedCommunes(false);
    }
  };

  // Fonction pour mettre à jour une commune
  const updateCommune = async (codePostal, updates) => {
    try {
      const { error } = await supabase
        .from('communes')
        .update(updates)
        .eq('code_postal', codePostal);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        setStatus('error');
        setMessage(t('common.error'));
        return false;
      }

      setStatus('success');
      setMessage(t('common.updateSuccess'));
      
      // Recharger les listes
      loadIncludedCommunes(currentPage);
      loadCommunesStats();
      
      return true;
    } catch (error) {
      console.error('Erreur:', error);
      setStatus('error');
      setMessage(t('common.error'));
      return false;
    }
  };

  // Charger les statistiques au montage et après chaque import
  useEffect(() => {
    if (currentUser) {
      loadCommunesStats();
      loadIncludedCommunes();
    }
  }, [currentUser, importStats]);

  // Debounce du filtre de distance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterDistance(filterDistance);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterDistance]);

  // Géocoder le code postal ou nom de commune saisi manuellement
  useEffect(() => {
    if (!searchInput.trim() || (filterLocation && communeName)) {
      setCommuneSuggestions([]);
      setShowSuggestions(false);
      setSearchError('');
      return;
    }

    const timer = setTimeout(async () => {
      const input = searchInput.trim();
      console.log(`[Manual Geocoding] Géocodage de: ${input}`);
      
      try {
        const isPostalCode = /^\d{5}$/.test(input);
        
        if (isPostalCode) {
          const { data, error } = await supabase
            .from('communes')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee, libelle_acheminement, autre')
            .eq('code_postal', input)
            .not('latitude', 'is', null)
            .not('longitude', 'is', null)
            .neq('ignore', true)
            .limit(10);

          if (error) {
            console.error('[Manual Geocoding] Erreur Supabase:', error);
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          if (data && data.length > 0) {
            if (data.length === 1) {
              const commune = data[0];
              setCommuneName(commune.nom_de_la_commune);
              setFilterLocation(commune.code_postal);
              setCommuneInseeCode(commune.code_commune_insee);
              setSelectedCommuneLines([commune]); // Stocker la ligne complète
              setSearchInput('');
              setShowSuggestions(false);
              setCommuneSuggestions([]);
              setSearchError('');
              setUserPosition({ 
                latitude: parseFloat(commune.latitude), 
                longitude: parseFloat(commune.longitude) 
              });
            } else {
              setCommuneSuggestions(data);
              setShowSuggestions(true);
              setSearchError('');
            }
          } else {
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
          }
        } else {
          const { data, error } = await supabase
            .from('communes')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee, libelle_acheminement, autre')
            .ilike('nom_de_la_commune', `%${input}%`)
            .not('latitude', 'is', null)
            .not('longitude', 'is', null)
            .neq('ignore', true)
            .limit(50);

          if (error) {
            console.error('[Manual Geocoding] Erreur Supabase:', error);
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          if (data && data.length > 0) {
            if (data.length === 1) {
              const commune = data[0];
              setFilterLocation(commune.code_postal);
              setCommuneName(commune.nom_de_la_commune);
              setCommuneInseeCode(commune.code_commune_insee);
              setSelectedCommuneLines([commune]); // Stocker la ligne complète
              setSearchInput('');
              setShowSuggestions(false);
              setCommuneSuggestions([]);
              setSearchError('');
              setUserPosition({ 
                latitude: parseFloat(commune.latitude), 
                longitude: parseFloat(commune.longitude) 
              });
            } else {
              setCommuneSuggestions(data);
              setShowSuggestions(true);
              setSearchError('');
            }
          } else {
            setCommuneSuggestions([]);
            setShowSuggestions(false);
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
          }
        }
      } catch (error) {
        console.error('[Manual Geocoding] Erreur lors du géocodage:', error);
        setSearchError(t('filters.noResults'));
        setTimeout(() => setSearchError(''), 3000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchInput, filterLocation, communeName, t]);

  // Récupérer les codes postaux à proximité
  useEffect(() => {
    const fetchNearbyPostalCodes = async () => {
      if (!userPosition) {
        setPostalCodesLoading(false);
        return;
      }

      setPostalCodesLoading(true);
      
      if (debouncedFilterDistance === 0 && filterLocation) {
        console.log('[PostalCodes] Distance = 0, commune:', communeName);
        
        // Si on a déjà les lignes de la commune sélectionnée, utiliser ces lignes directement
        if (selectedCommuneLines && selectedCommuneLines.length > 0) {
          console.log('[PostalCodes] Utilisation des lignes déjà chargées:', selectedCommuneLines.length);
          
          // Grouper par code postal avec toutes les lignes
          const groupedByPostalCode = selectedCommuneLines.reduce((acc, curr) => {
            if (!acc[curr.code_postal]) {
              acc[curr.code_postal] = {
                code_postal: curr.code_postal,
                communes: [],
                lines: []
              };
            }
            if (curr.nom_de_la_commune && !acc[curr.code_postal].communes.includes(curr.nom_de_la_commune)) {
              acc[curr.code_postal].communes.push(curr.nom_de_la_commune);
            }
            acc[curr.code_postal].lines.push({
              code_commune_insee: curr.code_commune_insee,
              code_postal: curr.code_postal,
              nom_de_la_commune: curr.nom_de_la_commune,
              libelle_acheminement: curr.libelle_acheminement,
              autre: curr.autre
            });
            return acc;
          }, {});
          
          const result = Object.values(groupedByPostalCode);
          console.log('[PostalCodes] Résultat groupé (distance 0):', result);
          console.log('[PostalCodes] Total lignes (distance 0):', result.reduce((sum, pc) => sum + (pc.lines?.length || 0), 0));
          
          setNearbyPostalCodes(result);
          setPostalCodesLoading(false);
          return;
        }
        
        // Sinon, requête par code postal (fallback)
        console.log('[PostalCodes] Requête par code postal:', filterLocation);
        const { data, error } = await supabase
          .from('communes')
          .select('code_postal, nom_de_la_commune, code_commune_insee, libelle_acheminement, autre')
          .eq('code_postal', filterLocation)
          .neq('ignore', true);
        
        if (error) {
          console.error('[PostalCodes] Erreur Supabase:', error);
          setPostalCodesLoading(false);
          return;
        }
        
        console.log('[PostalCodes] Données reçues (distance 0):', data);
        
        if (data && data.length > 0) {
          // Grouper par code postal avec toutes les lignes
          const groupedByPostalCode = data.reduce((acc, curr) => {
            if (!acc[curr.code_postal]) {
              acc[curr.code_postal] = {
                code_postal: curr.code_postal,
                communes: [],
                lines: []
              };
            }
            if (curr.nom_de_la_commune && !acc[curr.code_postal].communes.includes(curr.nom_de_la_commune)) {
              acc[curr.code_postal].communes.push(curr.nom_de_la_commune);
            }
            acc[curr.code_postal].lines.push({
              code_commune_insee: curr.code_commune_insee,
              code_postal: curr.code_postal,
              nom_de_la_commune: curr.nom_de_la_commune,
              libelle_acheminement: curr.libelle_acheminement,
              autre: curr.autre
            });
            return acc;
          }, {});
          
          const result = Object.values(groupedByPostalCode);
          console.log('[PostalCodes] Résultat groupé (distance 0):', result);
          console.log('[PostalCodes] Total lignes (distance 0):', result.reduce((sum, pc) => sum + (pc.lines?.length || 0), 0));
          
          setNearbyPostalCodes(result);
        }
        setPostalCodesLoading(false);
        return;
      }

      const distanceInMeters = debouncedFilterDistance * 1000;

      try {
        // Étape 1 : Récupérer les codes postaux via RPC
        console.log('[PostalCodes] Étape 1 : Appel RPC get_nearby_postal_codes');
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_nearby_postal_codes', {
          user_lat: userPosition.latitude,
          user_lon: userPosition.longitude,
          distance_meters: distanceInMeters
        });

        if (rpcError) {
          console.error('[PostalCodes] Erreur RPC:', rpcError);
          setPostalCodesLoading(false);
          return;
        }
        
        console.log('[PostalCodes] Données RPC reçues:', rpcData);
        console.log('[PostalCodes] Nombre de lignes RPC:', rpcData?.length);
        
        // Extraire les codes postaux uniques
        const uniquePostalCodes = [...new Set(rpcData?.map(row => row.code_postal).filter(Boolean))];
        console.log('[PostalCodes] Codes postaux uniques:', uniquePostalCodes);
        console.log('[PostalCodes] Nombre de codes postaux uniques:', uniquePostalCodes.length);
        
        if (uniquePostalCodes.length === 0) {
          console.log('[PostalCodes] Aucun code postal trouvé');
          setNearbyPostalCodes([]);
          setPostalCodesLoading(false);
          return;
        }
        
        // Étape 2 : Récupérer TOUTES les communes pour ces codes postaux
        console.log('[PostalCodes] Étape 2 : Récupération des communes pour', uniquePostalCodes.length, 'codes postaux');
        const { data: communesData, error: communesError } = await supabase
          .from('communes')
          .select('code_postal, nom_de_la_commune, code_commune_insee, libelle_acheminement, autre')
          .in('code_postal', uniquePostalCodes)
          .not('nom_de_la_commune', 'is', null)
          .neq('ignore', true)
          .order('code_postal');
        
        if (communesError) {
          console.error('[PostalCodes] Erreur récupération communes:', communesError);
          setPostalCodesLoading(false);
          return;
        }
        
        console.log('[PostalCodes] Communes récupérées depuis la table:', communesData);
        console.log('[PostalCodes] Nombre total de lignes communes:', communesData?.length);
        
        // Grouper par code postal avec toutes les lignes
        const groupedByPostalCode = {};
        
        communesData?.forEach(row => {
          console.log('[PostalCodes] Traitement ligne:', row);
          
          if (!groupedByPostalCode[row.code_postal]) {
            groupedByPostalCode[row.code_postal] = {
              code_postal: row.code_postal,
              communes: [],
              lines: [] // Stocker toutes les lignes complètes
            };
          }
          
          if (row.nom_de_la_commune && !groupedByPostalCode[row.code_postal].communes.includes(row.nom_de_la_commune)) {
            groupedByPostalCode[row.code_postal].communes.push(row.nom_de_la_commune);
            console.log(`[PostalCodes] Ajout commune "${row.nom_de_la_commune}" au CP ${row.code_postal}`);
          }
          
          // Stocker la ligne complète avec tous les champs
          groupedByPostalCode[row.code_postal].lines.push({
            code_commune_insee: row.code_commune_insee,
            code_postal: row.code_postal,
            nom_de_la_commune: row.nom_de_la_commune,
            libelle_acheminement: row.libelle_acheminement,
            autre: row.autre
          });
        });
        
        const result = Object.values(groupedByPostalCode);
        
        console.log('[PostalCodes] ===== RÉSULTAT FINAL =====');
        console.log('[PostalCodes] Nombre de codes postaux:', result.length);
        console.log('[PostalCodes] Total communes:', result.reduce((sum, pc) => sum + pc.communes.length, 0));
        console.log('[PostalCodes] Total lignes:', result.reduce((sum, pc) => sum + (pc.lines?.length || 0), 0));
        console.log('[PostalCodes] Détail par code postal:');
        result.forEach(pc => {
          console.log(`  CP ${pc.code_postal}: ${pc.communes.length} commune(s) = [${pc.communes.join(', ')}], ${pc.lines?.length || 0} ligne(s)`);
        });
        console.log('[PostalCodes] ========================');
        
        setNearbyPostalCodes(result);
      } catch (error) {
        console.error('[PostalCodes] Erreur lors de la récupération des codes postaux:', error);
      } finally {
        setPostalCodesLoading(false);
      }
    };

    fetchNearbyPostalCodes();
  }, [userPosition, debouncedFilterDistance, filterLocation]);

  // Sélectionner une commune
  const handleSelectCommune = async (commune) => {
    console.log('[Manual Geocoding] Commune sélectionnée:', commune);
    
    // Récupérer toutes les lignes STRICTEMENT pour ce nom de commune
    const { data, error } = await supabase
      .from('communes')
      .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee, libelle_acheminement, autre')
      .eq('nom_de_la_commune', commune.nom_de_la_commune)
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .neq('ignore', true);
    
    if (error || !data || data.length === 0) {
      console.error('[Manual Geocoding] Erreur récupération communes par nom:', error);
      setSearchError(t('filters.noResults'));
      setTimeout(() => setSearchError(''), 3000);
      return;
    }
    
    console.log('[Manual Geocoding] Lignes trouvées pour la commune', commune.nom_de_la_commune, ':', data);
    
    // Utiliser la première ligne pour les coordonnées
    const firstCommune = data[0];
    
    setFilterLocation(firstCommune.code_postal);
    setCommuneName(commune.nom_de_la_commune);
    setCommuneInseeCode(firstCommune.code_commune_insee);
    setSelectedCommuneLines(data); // Stocker toutes les lignes strictement pour ce nom de commune
    setSearchInput('');
    setShowSuggestions(false);
    setCommuneSuggestions([]);
    setSearchError('');
    setUserPosition({ 
      latitude: parseFloat(firstCommune.latitude), 
      longitude: parseFloat(firstCommune.longitude) 
    });
  };

  // Supprimer la localisation
  const handleClearLocation = () => {
    setFilterLocation('');
    setCommuneName('');
    setCommuneInseeCode('');
    setSelectedCommuneLines([]);
    setSearchInput('');
    setUserPosition(null);
    setNearbyPostalCodes([]);
    setCommuneSuggestions([]);
    setShowSuggestions(false);
    setSearchError('');
  };

  // Mettre à jour les communes
  const handleBulkImport = async () => {
    if (!currentUser) {
      setStatus('error');
      setMessage(t('common.notAuthenticated'));
      return;
    }

    if (nearbyPostalCodes.length === 0) {
      setStatus('error');
      setMessage(t('settings.admin.importCommune.noPostalCodes'));
      return;
    }

    setLoadingImport(true);
    setStatus('');
    setMessage('');
    setImportStats(null);

    try {
      const postalCodes = nearbyPostalCodes.map(pc => pc.code_postal);
      
      // Mettre à jour la table communes (en excluant les communes avec ignore=true)
      const { data, error } = await supabase
        .from('communes')
        .update({
          include: includeMode,
          freshness: freshnessDays
        })
        .in('code_postal', postalCodes)
        .neq('ignore', true)
        .select();

      if (error) {
        console.error("Erreur lors de la mise à jour des communes:", error);
        setStatus('error');
        setMessage(error.message || t('settings.admin.importCommune.status.error'));
        return;
      }

      setStatus('success');
      setMessage(t('settings.admin.importCommune.bulkImportSuccess'));
      setImportStats({
        total: postalCodes.length,
        updated: data?.length || 0,
        include: includeMode,
        freshness: freshnessDays
      });

    } catch (error) {
      console.error("Erreur lors de la mise à jour en masse:", error);
      setStatus('error');
      setMessage(error.message || t('settings.admin.importCommune.status.error'));
    } finally {
      setLoadingImport(false);
    }
  };

  // Efface le message après 5 secondes
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
        setStatus('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Récupérer les polygones des communes incluses
  useEffect(() => {
    const fetchCommunesPolygons = async () => {
      setLoadingPolygons(true);
      try {
        let includedCommunes;
        
        // Si une commune est sélectionnée avec un rayon > 0, afficher tous les codes postaux à proximité
        if (communeName && selectedCommuneLines.length > 0 && nearbyPostalCodes.length > 0 && filterDistance > 0) {
          // Extraire toutes les lignes de tous les codes postaux à proximité
          const allLines = nearbyPostalCodes.flatMap(pc => pc.lines || []);
          
          if (allLines.length === 0) {
            setCommunesPolygons([]);
            setEventsCount({});
            return;
          }
          
          // Extraire les codes postaux uniques
          const codesPostaux = [...new Set(allLines.map(line => line.code_postal).filter(Boolean))];
          
          // Récupérer toutes les lignes correspondant à ces codes postaux
          const { data, error } = await supabase
            .from('communes')
            .select('code_postal, nom_de_la_commune, code_commune_insee, libelle_acheminement, autre')
            .in('code_postal', codesPostaux)
            .neq('ignore', true);
          
          if (error || !data || data.length === 0) {
            setCommunesPolygons([]);
            setEventsCount({});
            return;
          }
          
          includedCommunes = data;
        } else if (communeName && selectedCommuneLines.length > 0) {
          // Si distance = 0, afficher UNIQUEMENT la commune sélectionnée
          const nomsCommunesSelectionnes = selectedCommuneLines
            .map(line => line.nom_de_la_commune)
            .filter(Boolean);
          
          if (nomsCommunesSelectionnes.length === 0) {
            setCommunesPolygons([]);
            setEventsCount({});
            return;
          }
          
          // Récupérer les lignes de la commune sélectionnée (sans filtre include)
          const { data, error } = await supabase
            .from('communes')
            .select('code_postal, nom_de_la_commune, code_commune_insee, libelle_acheminement, autre')
            .in('nom_de_la_commune', nomsCommunesSelectionnes)
            .neq('ignore', true);
          
          if (error || !data || data.length === 0) {
            setCommunesPolygons([]);
            setEventsCount({});
            return;
          }
          
          includedCommunes = data;
        } else {
          // Sinon, afficher toutes les communes avec include=true
          const { data, error } = await supabase
            .from('communes')
            .select('code_postal, nom_de_la_commune, code_commune_insee, libelle_acheminement, autre')
            .eq('include', true)
            .neq('ignore', true);
          
          if (error || !data || data.length === 0) {
            setCommunesPolygons([]);
            setEventsCount({});
            return;
          }
          
          includedCommunes = data;
        }

        // Extraire les codes INSEE uniques
        const inseeSet = new Set(includedCommunes.map(c => c.code_commune_insee).filter(Boolean));
        const inseeCodes = Array.from(inseeSet);

        // Récupérer TOUS les codes postaux pour ces communes INSEE en une seule requête
        const { data: allPostalCodes, error: postalsError } = await supabase
          .from('communes')
          .select('code_commune_insee, code_postal')
          .in('code_commune_insee', inseeCodes)
          .not('code_postal', 'is', null)
          .neq('ignore', true);

        if (postalsError) return;

        // Grouper les codes postaux par INSEE
        const inseeToPostalCodes = allPostalCodes.reduce((acc, row) => {
          if (!acc[row.code_commune_insee]) acc[row.code_commune_insee] = [];
          acc[row.code_commune_insee].push(row.code_postal);
          return acc;
        }, {});

        // Extraire tous les codes postaux uniques
        const allPostals = [...new Set(allPostalCodes.map(c => c.code_postal))];

        // Récupérer tous les événements en une seule requête
        const { data: events } = await supabase
          .from('events')
          .select('postal_code')
          .in('postal_code', allPostals);

        // Compter les événements par code postal
        const eventsByPostal = (events || []).reduce((acc, e) => {
          if (e.postal_code) acc[e.postal_code] = (acc[e.postal_code] || 0) + 1;
          return acc;
        }, {});

        // Sommer par INSEE
        const eventsByInsee = {};
        for (const [insee, postals] of Object.entries(inseeToPostalCodes)) {
          eventsByInsee[insee] = postals.reduce((sum, cp) => sum + (eventsByPostal[cp] || 0), 0);
        }

        // Grouper les noms de communes par INSEE
        const communesByInsee = includedCommunes.reduce((acc, c) => {
          if (!c.code_commune_insee) return acc;
          if (!acc[c.code_commune_insee]) acc[c.code_commune_insee] = new Set();
          if (c.nom_de_la_commune) acc[c.code_commune_insee].add(c.nom_de_la_commune);
          return acc;
        }, {});

        // Récupérer les polygones
        const uniquePostalCodes = [...new Set(includedCommunes.map(c => c.code_postal).filter(Boolean))];
        
        // Construire la requête pour les polygones
        let polygonsQuery = supabase
          .from('communes')
          .select('code_postal, geom, code_commune_insee, nom_de_la_commune, libelle_acheminement, autre')
          .in('code_postal', uniquePostalCodes)
          .not('geom', 'is', null);
        
        // Si un filtre est actif avec distance = 0, filtrer strictement par nom de commune
        if (communeName && selectedCommuneLines.length > 0 && filterDistance === 0) {
          const nomsCommunesSelectionnes = selectedCommuneLines
            .map(line => line.nom_de_la_commune)
            .filter(Boolean);
          
          if (nomsCommunesSelectionnes.length > 0) {
            polygonsQuery = polygonsQuery.in('nom_de_la_commune', nomsCommunesSelectionnes);
          }
        }
        
        const { data: polygons } = await polygonsQuery;

        if (!polygons) return;

        // Enrichir les polygones
        const enrichedPolygons = polygons.map(p => ({
          ...p,
          nom: communesByInsee[p.code_commune_insee] 
            ? Array.from(communesByInsee[p.code_commune_insee]).join(', ')
            : t('common.unknown')
        }));

        setCommunesPolygons(enrichedPolygons);

        // Mapper les événements par code postal pour l'affichage
        const eventsCountByPostalCode = {};
        enrichedPolygons.forEach(p => {
          eventsCountByPostalCode[p.code_postal] = eventsByInsee[p.code_commune_insee] || 0;
        });

        setEventsCount(eventsCountByPostalCode);
      } catch (error) {
        console.error('Erreur chargement polygones:', error);
      } finally {
        setLoadingPolygons(false);
      }
    };

    fetchCommunesPolygons();
  }, [importStats, communeName, selectedCommuneLines, nearbyPostalCodes, filterDistance, t]);

  // Fonction pour obtenir la couleur selon le nombre d'événements
  const getColorByEventCount = (count, hasActiveFilter = false) => {
    // Si pas d'événements
    if (!count || count === 0) {
      // Si filtre actif, afficher en vert
      if (hasActiveFilter) return '#22c55e';
      // Sinon, afficher en orange
      return '#fb923c';
    }
    
    // Dégradé du bleu clair/blanc vers le bleu foncé/noir
    const maxCount = 50;
    const ratio = Math.min(count / maxCount, 1);
    
    // Couleur de départ (bleu très clair, proche du blanc) : rgb(224, 242, 254)
    const startR = 224;
    const startG = 242;
    const startB = 254;
    
    // Couleur d'arrivée (bleu très foncé, proche du noir) : rgb(3, 39, 54)
    const endR = 3;
    const endG = 39;
    const endB = 54;
    
    // Interpolation avec courbe exponentielle pour une transition marquée
    const adjustedRatio = Math.pow(ratio, 0.7);
    const r = Math.round(startR + (endR - startR) * adjustedRatio);
    const g = Math.round(startG + (endG - startG) * adjustedRatio);
    const b = Math.round(startB + (endB - startB) * adjustedRatio);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Initialiser et mettre à jour la carte
  useEffect(() => {
    if (!mapRef.current) return;

    // Initialiser la carte si nécessaire
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([46.603354, 1.888334], 6);

      const baseLayers = {
        osm: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }),
        stadiaSmooth: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaSmoothDark: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stradiamaps: L.tileLayer(`https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaOSMBright: L.tileLayer(`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        satellite: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: '&copy; <a href="https://www.esri.com">Esri</a>'
        }),
        terrain: L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        })
      };

      const layerNames = {
        osm: t('map.standard'),
        stadiaSmooth: t('map.stadiaSmooth'),
        stadiaSmoothDark: t('map.stadiaSmoothDark'),
        stradiamaps: t('map.stadiaOutdoors'),
        stadiaOSMBright: t('map.stadiaOSMBright'),
        satellite: t('map.satellite'),
        terrain: t('map.terrain')
      };
      const selectedProvider = baseLayers[tileProvider] ? tileProvider : 'osm';
      baseLayers[selectedProvider].addTo(mapInstanceRef.current);
      L.control.layers(
        Object.fromEntries(Object.entries(baseLayers).map(([key, layer]) => [layerNames[key], layer])),
        undefined,
        { position: 'topright' }
      ).addTo(mapInstanceRef.current);
    }

    // Nettoyer les couches existantes
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.Circle || layer instanceof L.Marker) {
        mapInstanceRef.current.removeLayer(layer);
      }
    });

    const bounds = L.latLngBounds([]);

    // 1. Toujours afficher les communes incluses (fond de carte)
    if (communesPolygons.length > 0) {
      // Déterminer si un filtre est actif (lieu sélectionné)
      const hasActiveFilter = communeName && selectedCommuneLines.length > 0;
      
      communesPolygons.forEach(commune => {
        try {
          const geojson = typeof commune.geom === 'string' 
            ? JSON.parse(commune.geom) 
            : commune.geom;

          const communeName = commune.nom || commune.nom_de_la_commune || t('common.unknown');
          const postalCode = commune.code_postal || '';
          const inseeCode = commune.code_commune_insee || '';
          const libelle = commune.libelle_acheminement || '';
          const autre = commune.autre || '';
          
          let popupContent, color, styleConfig;
          
          if (hasActiveFilter) {
            // Mode filtre actif : popup simple avec événements et couleur verte uniforme
            const eventCount = eventsCount[postalCode] || 0;
            popupContent = `<div style="font-size: 12px; line-height: 1.4;">`;
            if (inseeCode) popupContent += `<div><strong>Code INSEE:</strong> ${inseeCode}</div>`;
            if (postalCode) popupContent += `<div><strong>Code Postal:</strong> ${postalCode}</div>`;
            if (libelle) popupContent += `<div><strong>Acheminement:</strong> ${libelle}</div>`;
            if (autre) popupContent += `<div><strong>Autre:</strong> ${autre}</div>`;
            popupContent += `<div style="margin-top: 4px;"><strong>Événements:</strong> ${eventCount}</div>`;
            popupContent += `</div>`;
            
            color = '#22c55e';
            styleConfig = {
              color: '#22c55e',
              weight: 2,
              opacity: 0.7,
              fillOpacity: 0.15,
              fillColor: '#22c55e'
            };
          } else {
            // Mode normal : popup avec événements et dégradé bleu
            const eventCount = eventsCount[postalCode] || 0;
            color = getColorByEventCount(eventCount, false);
            
            popupContent = `<div style="font-size: 12px; line-height: 1.4;">`;
            popupContent += `<div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">${communeName}</div>`;
            if (inseeCode) popupContent += `<div><strong>INSEE:</strong> ${inseeCode}</div>`;
            if (postalCode) popupContent += `<div><strong>Code Postal:</strong> ${postalCode}</div>`;
            if (libelle) popupContent += `<div><strong>Libellé:</strong> ${libelle}</div>`;
            if (autre) popupContent += `<div><strong>Autre:</strong> ${autre}</div>`;
            popupContent += `<div style="margin-top: 4px;"><strong>Événements:</strong> ${eventCount}</div>`;
            popupContent += `</div>`;
            
            styleConfig = {
              color: color,
              weight: 2,
              opacity: 1,
              fillOpacity: 0.4,
              fillColor: color
            };
          }
          
          const geoJsonLayer = L.geoJSON(geojson, {
            style: styleConfig
          }).bindPopup(popupContent);

          geoJsonLayer.addTo(mapInstanceRef.current);
          bounds.extend(geoJsonLayer.getBounds());

          // Marqueurs uniquement en mode normal (pas de filtre actif)
          if (!hasActiveFilter && showMarkers) {
            const eventCount = eventsCount[postalCode] || 0;
            const center = geoJsonLayer.getBounds().getCenter();
            
            const divIcon = L.divIcon({
              className: 'event-count-marker',
              html: `<div style="
                background: ${color};
                color: white;
                border: 3px solid white;
                border-radius: 50%;
                width: ${Math.min(70, 35 + Math.sqrt(eventCount) * 4)}px;
                height: ${Math.min(70, 35 + Math.sqrt(eventCount) * 4)}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: ${eventCount > 99 ? '11px' : '15px'};
                box-shadow: 0 4px 8px rgba(0,0,0,0.4);
                transition: all 0.3s ease;
              ">${eventCount}</div>`,
              iconSize: [Math.min(70, 35 + Math.sqrt(eventCount) * 4), Math.min(70, 35 + Math.sqrt(eventCount) * 4)],
              iconAnchor: [Math.min(70, 35 + Math.sqrt(eventCount) * 4) / 2, Math.min(70, 35 + Math.sqrt(eventCount) * 4) / 2]
            });

            const marker = L.marker(center, { icon: divIcon });
            let tooltipContent = `${communeName}`;
            if (inseeCode) tooltipContent += `<br>INSEE: ${inseeCode}`;
            if (postalCode) tooltipContent += `<br>CP: ${postalCode}`;
            if (libelle) tooltipContent += `<br>${libelle}`;
            tooltipContent += `<br>${eventCount} ${t('settings.admin.importCommune.events') || 'événements'}`;
            marker.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top'
            });
            marker.addTo(mapInstanceRef.current);
          }
        } catch (error) {
          console.error('[Polygons] Erreur parsing géométrie:', error, commune);
        }
      });
    }

    // 2. Ajouter le cercle de rayon si une position est sélectionnée - VERT
    if (userPosition && filterDistance > 0) {
      radiusCircleRef.current = L.circle([userPosition.latitude, userPosition.longitude], {
        radius: filterDistance * 1000,
        color: '#22c55e',
        weight: 2,
        opacity: 0.5,
        fillOpacity: 0.02,
        fillColor: '#22c55e',
        interactive: false  // Ne pas capturer les clics
      }).addTo(mapInstanceRef.current);
      
      bounds.extend(radiusCircleRef.current.getBounds());
    }

    // 3. Ajouter les polygones des codes postaux à proximité PAR-DESSUS - VERTS TRANSPARENTS
    if (nearbyPostalCodes.length > 0) {
      const fetchAndDisplayPolygons = async () => {
        const postalCodes = nearbyPostalCodes.map(pc => pc.code_postal);
        
        // Vérifier que la carte existe toujours
        if (!mapInstanceRef.current) {
          console.warn('[Map] Instance carte non disponible');
          return;
        }

        // Construire la requête pour les polygones à proximité
        let polygonsQuery = supabase
          .from('communes')
          .select('code_postal, geom, code_commune_insee, nom_de_la_commune, libelle_acheminement, autre')
          .in('code_postal', postalCodes)
          .not('geom', 'is', null);
        
        // Si un filtre est actif, filtrer strictement par nom de commune
        if (communeName && selectedCommuneLines.length > 0) {
          const nomsCommunesSelectionnes = selectedCommuneLines
            .map(line => line.nom_de_la_commune)
            .filter(Boolean);
          
          if (nomsCommunesSelectionnes.length > 0) {
            polygonsQuery = polygonsQuery.in('nom_de_la_commune', nomsCommunesSelectionnes);
          }
        }
        
        const { data: polygonsData, error: polygonsError } = await polygonsQuery;

        if (polygonsError) {
          console.error('[Map] Erreur récupération polygones:', polygonsError);
          return;
        }
        
        // Vérifier à nouveau que la carte existe avant d'ajouter les couches
        if (!mapInstanceRef.current) {
          console.warn('[Map] Instance carte non disponible après récupération des données');
          return;
        }

        polygonsData?.forEach(commune => {
          try {
            const geojson = typeof commune.geom === 'string' 
              ? JSON.parse(commune.geom) 
              : commune.geom;

            const communesInfo = nearbyPostalCodes.find(pc => pc.code_postal === commune.code_postal);
            const postalCode = commune.code_postal || '';
            const inseeCode = commune.code_commune_insee || '';
            const libelle = commune.libelle_acheminement || '';
            const autre = commune.autre || '';
            const eventCount = eventsCount[postalCode] || 0;
            // Construire le contenu du popup avec événements
            let popupContent = `<div style="font-size: 12px; line-height: 1.4;">`;
            if (inseeCode) popupContent += `<div><strong>Code INSEE:</strong> ${inseeCode}</div>`;
            if (postalCode) popupContent += `<div><strong>Code Postal:</strong> ${postalCode}</div>`;
            if (libelle) popupContent += `<div><strong>Acheminement:</strong> ${libelle}</div>`;
            if (autre) popupContent += `<div><strong>Autre:</strong> ${autre}</div>`;
            popupContent += `<div style="margin-top: 4px;"><strong>Événements:</strong> ${eventCount}</div>`;
            popupContent += `</div>`;
            
            // Polygones verts transparents simples
            const geoJsonLayer = L.geoJSON(geojson, {
              style: {
                color: '#22c55e',
                weight: 2,
                opacity: 0.7,
                fillOpacity: 0.15,
                fillColor: '#22c55e'
              }
            }).bindPopup(popupContent);

            // Vérifier que la carte existe avant d'ajouter la couche
            if (mapInstanceRef.current) {
              geoJsonLayer.addTo(mapInstanceRef.current);
              bounds.extend(geoJsonLayer.getBounds());
            }
          } catch (error) {
            console.error('[Map] Erreur parsing géométrie:', error, commune);
          }
        });

        if (bounds.isValid() && mapInstanceRef.current) {
          mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      };

      fetchAndDisplayPolygons();
    } else {
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      } else if (userPosition) {
        mapInstanceRef.current.setView([userPosition.latitude, userPosition.longitude], 12);
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        radiusCircleRef.current = null;
      }
    };
  }, [communesPolygons, nearbyPostalCodes, userPosition, filterDistance, includeMode, eventsCount, showMarkers, tileProvider, t]);

  if (adminLoading || !authChecked) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('settings.admin.importCommune.title')} backPath="/settings" />

      <div className="p-4">
        {!currentUser ? (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
            <p className="text-amber-700">{t('common.pleaseLogin')}</p>
            <button 
              className="mt-2 bg-[#2894D8] text-white px-4 py-2 rounded hover:bg-[#136E9D] transition-colors"
              onClick={() => window.location.href = '/login'}
            >
              {t('common.login')}
            </button>
          </div>
        ) : (
          <>


            {/* Sélection de la commune */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <label className="block text-sm font-medium mb-2">{t('filters.location')}</label>
              <div className="relative">
                {filterLocation && selectedCommuneLines.length > 0 ? (
                  <div className="w-full border rounded-lg bg-[#2894D8] bg-opacity-10 border-[#2894D8]">
                    <div className="flex items-center justify-between p-2 border-b border-[#2894D8] border-opacity-30">
                      <span className="text-sm font-semibold text-gray-900">
                        {t('filters.location')} : {filterLocation}
                      </span>
                      <button
                        onClick={handleClearLocation}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={t('common.clear')}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
                      {selectedCommuneLines.map((line, index) => (
                        <div key={index} className="text-xs text-gray-700 bg-white bg-opacity-50 p-2 rounded border border-[#2894D8] border-opacity-20">
                          <div className="font-medium">
                            INSEE: {line.code_commune_insee || 'N/A'} | CP: {line.code_postal || 'N/A'}
                          </div>
                          <div>
                            Commune: {line.nom_de_la_commune || 'N/A'}
                          </div>
                          {line.libelle_acheminement && (
                            <div>
                              Libellé: {line.libelle_acheminement}
                            </div>
                          )}
                          {line.autre && (
                            <div>
                              Autre: {line.autre}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <input 
                    type="text" 
                    placeholder={t('filters.locationPlaceholder')}
                    className="w-full p-2 border rounded-lg"
                    value={searchInput}
                    onChange={e => {
                      setSearchInput(e.target.value);
                      setSearchError('');
                    }}
                    onFocus={() => {
                      if (communeSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                  />
                )}
                {searchError && (
                  <div className="absolute left-0 right-0 top-full mt-1 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {searchError}
                  </div>
                )}
                {showSuggestions && communeSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
                    {communeSuggestions.map((commune, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectCommune(commune)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-sm">{commune.nom_de_la_commune}</div>
                        <div className="text-xs text-gray-500">{commune.code_postal}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sélection du rayon */}
            {userPosition && (
              <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                <label className="block text-sm font-medium mb-2">
                  {t('filters.distance')} : {filterDistance} km
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="500" 
                  step="1"
                  className="w-full accent-[#2894D8]"
                  value={filterDistance}
                  onChange={e => setFilterDistance(Number(e.target.value))}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}

            {/* Affichage des codes postaux */}
            {userPosition && nearbyPostalCodes.length > 0 && (
              <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                <button
                  onClick={() => setShowPostalCodesList(!showPostalCodesList)}
                  className="w-full text-left flex items-center justify-between font-medium mb-2"
                >
                  <span>
                    {nearbyPostalCodes.length} {nearbyPostalCodes.length > 1 ? t('filters.postalCodesFound') : t('filters.postalCodesFound').replace('s', '')} ({nearbyPostalCodes.reduce((sum, pc) => sum + (pc.lines?.length || 0), 0)} lignes)
                  </span>
                  <span>{showPostalCodesList ? '▲' : '▼'}</span>
                </button>
                {showPostalCodesList && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm max-h-96 overflow-y-auto">
                    {nearbyPostalCodes.map((pc) => (
                      <div key={pc.code_postal} className="mb-3 pb-3 border-b border-gray-300 last:border-b-0">
                        <div className="font-semibold text-base text-[#2894D8] mb-2">
                          Code Postal: {pc.code_postal}
                        </div>
                        {pc.lines && pc.lines.length > 0 ? (
                          <div className="space-y-2">
                            {pc.lines.map((line, index) => (
                              <div key={index} className="bg-white p-2 rounded border border-gray-200 text-xs text-gray-700">
                                {line.code_commune_insee && (
                                  <div><strong>INSEE:</strong> {line.code_commune_insee}</div>
                                )}
                                {line.nom_de_la_commune && (
                                  <div><strong>Commune:</strong> {line.nom_de_la_commune}</div>
                                )}
                                {line.libelle_acheminement && (
                                  <div><strong>Libellé:</strong> {line.libelle_acheminement}</div>
                                )}
                                {line.autre && (
                                  <div><strong>Autre:</strong> {line.autre}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-gray-500 italic">
                            {pc.communes?.join(', ') || t('common.unknown')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sélection du mode include/exclude */}
            {userPosition && nearbyPostalCodes.length > 0 && (
              <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                <label className="block text-sm font-medium mb-2">
                  {t('settings.admin.importCommune.includeMode')}
                </label>
                <div className="flex items-center space-x-3">
                  <span className={`text-sm ${!includeMode ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                    {t('settings.admin.importCommune.exclude')}
                  </span>
                  <button
                    onClick={() => setIncludeMode(!includeMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2894D8] focus:ring-offset-2 ${
                      includeMode ? 'bg-[#2894D8]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        includeMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm ${includeMode ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                    {t('settings.admin.importCommune.include')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.admin.importCommune.includeModeHelp')}
                </p>
              </div>
            )}

            {/* Sélection de la fraîcheur */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <label className="block text-sm font-medium mb-2">
                {t('settings.admin.importCommune.freshness')}
              </label>
              <select 
                className="w-full p-2 border rounded-lg"
                value={freshnessDays}
                onChange={e => setFreshnessDays(Number(e.target.value))}
              >
                <option value={7}>7 {t('settings.admin.importCommune.days')}</option>
                <option value={15}>15 {t('settings.admin.importCommune.days')}</option>
                <option value={30}>30 {t('settings.admin.importCommune.days')}</option>
                <option value={45}>45 {t('settings.admin.importCommune.days')}</option>
                <option value={60}>60 {t('settings.admin.importCommune.days')}</option>
                <option value={75}>75 {t('settings.admin.importCommune.days')}</option>
                <option value={90}>90 {t('settings.admin.importCommune.days')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.admin.importCommune.freshnessHelp')}
              </p>
            </div>

            {/* Bouton d'import */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <button
                className="w-full bg-[#2894D8] text-white px-4 py-3 rounded hover:bg-[#136E9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleBulkImport}
                disabled={loadingImport || nearbyPostalCodes.length === 0 || postalCodesLoading}
              >
                {loadingImport
                  ? t('common.loading')
                  : t('settings.admin.importCommune.updateCommunes')}
              </button>
              
              {message && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${
                  status === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  status === 'info' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
                  'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {message}
                  {importStats && (
                    <div className="mt-2 text-xs">
                      <div>{t('settings.admin.importCommune.total')}: {importStats.total}</div>
                      <div>{t('settings.admin.importCommune.updated')}: {importStats.updated}</div>
                      <div>{t('settings.admin.importCommune.include')}: {importStats.include ? t('common.yes') : t('common.no')}</div>
                      <div>{t('settings.admin.importCommune.freshness')}: {importStats.freshness} {t('settings.admin.importCommune.days')}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bouton pour voir les jobs */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <button
                onClick={() => navigate('/admin/jobs-management')}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
              >
                {t('settings.admin.importCommune.viewJobs')}
              </button>
            </div>

            {/* Statistiques des communes avec include=true */}
            {communesStats && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-h3 text-gray-700 mb-2">
                  📊 {communesStats.total} {t('settings.admin.jobsManagement.communesToInclude')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(communesStats.byFreshness)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([freshness, count]) => (
                      <div key={freshness} className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-600">
                          {freshness} {t('settings.admin.importCommune.days')}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {count}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {/* Carte des communes incluses */}
            <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-h2">
                  {t('settings.admin.importCommune.includedCommunesMap')}
                </h2>
                <button
                  onClick={() => setShowMarkers(!showMarkers)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    showMarkers 
                      ? 'bg-[#2894D8] text-white hover:bg-[#136E9D]' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {showMarkers ? t('settings.admin.importCommune.hideMarkers') : t('settings.admin.importCommune.showMarkers')}
                </button>
              </div>
              {loadingPolygons ? (
                <div className="flex items-center justify-center h-[600px]">
                  <LoadingSpinner />
                </div>
              ) : communesPolygons.length === 0 ? (
                <div className="h-[600px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500">{t('settings.admin.importCommune.noIncludedCommunes')}</p>
                </div>
              ) : (
                <div 
                  ref={mapRef} 
                  className="h-[600px] rounded-lg border border-gray-200"
                  style={{ zIndex: 1 }}
                />
              )}
              {communesPolygons.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {communesPolygons.length} {communesPolygons.length > 1 ? t('settings.admin.importCommune.communes') : t('settings.admin.importCommune.commune')} {t('settings.admin.importCommune.displayed')}
                </p>
              )}
            </div>
            
            {/* Liste des communes incluses */}
            <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
              <h2 className="text-h2 mb-4">
                {t('settings.admin.importCommune.includedCommunesList')}
              </h2>
              
              {loadingIncludedCommunes ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : includedCommunes.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  {t('settings.admin.importCommune.noIncludedCommunes')}
                </div>
              ) : (
                <>
                  {/* Table des communes */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('settings.admin.importCommune.inseecode')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('settings.admin.importCommune.commune')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('settings.admin.importCommune.freshness')}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {includedCommunes.map((commune) => (
                          <tr key={`${commune.code_postal}-${commune.nom_de_la_commune}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {commune.code_postal}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {commune.nom_de_la_commune}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {editingCommune === commune.code_postal ? (
                                <select
                                  className="p-1 border rounded text-sm"
                                  defaultValue={commune.freshness}
                                  onChange={async (e) => {
                                    const newFreshness = Number(e.target.value);
                                    const success = await updateCommune(commune.code_postal, {
                                      freshness: newFreshness
                                    });
                                    if (success) {
                                      setEditingCommune(null);
                                    }
                                  }}
                                  onBlur={() => setEditingCommune(null)}
                                  autoFocus
                                >
                                  <option value={7}>7 {t('settings.admin.importCommune.days')}</option>
                                  <option value={15}>15 {t('settings.admin.importCommune.days')}</option>
                                  <option value={30}>30 {t('settings.admin.importCommune.days')}</option>
                                  <option value={45}>45 {t('settings.admin.importCommune.days')}</option>
                                  <option value={60}>60 {t('settings.admin.importCommune.days')}</option>
                                  <option value={75}>75 {t('settings.admin.importCommune.days')}</option>
                                  <option value={90}>90 {t('settings.admin.importCommune.days')}</option>
                                </select>
                              ) : (
                                <button
                                  onClick={() => setEditingCommune(commune.code_postal)}
                                  className="text-left hover:text-[#2894D8] transition-colors"
                                >
                                  {commune.freshness} {t('settings.admin.importCommune.days')}
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                              <button
                                onClick={async () => {
                                  if (window.confirm(t('settings.admin.importCommune.confirmExclude'))) {
                                    await updateCommune(commune.code_postal, {
                                      include: false
                                    });
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 font-medium transition-colors"
                              >
                                {t('settings.admin.importCommune.exclude')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {t('common.page')} {currentPage} {t('common.of')} {totalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => loadIncludedCommunes(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 border rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                        >
                          {t('common.previous')}
                        </button>
                        <button
                          onClick={() => loadIncludedCommunes(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 border rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>





          </>
        )}
      </div>
    </div>
  );
};

export default ImportCommunePage;
